import { useEffect, useState } from 'react';
import { Loader2, Phone, CheckCircle2, User, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { usePhoneChannelSenders } from './newConversation/useChannelSenders';
import { useCrmResolver } from './newConversation/useCrmResolver';
import type { NewConversationDialogBaseProps } from './newConversation/types';

interface CreateConversationResponse {
  conversation_id: string;
}

export default function NewSmsConversationDialog({ open, onClose, onSuccess }: NewConversationDialogBaseProps) {
  const { toast } = useToast();
  const { senders, defaultId } = usePhoneChannelSenders(open, 'sms');
  const [selectedSenderId, setSelectedSenderId] = useState<string>('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactName, setContactName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && defaultId && !selectedSenderId) setSelectedSenderId(defaultId);
  }, [open, defaultId, selectedSenderId]);

  useEffect(() => {
    if (!open) {
      setSelectedSenderId('');
      setContactPhone('');
      setContactName('');
      setMessage('');
    }
  }, [open]);

  const { resolved, resolving } = useCrmResolver(contactPhone, open);

  useEffect(() => {
    if (resolved && !contactName) {
      const name = resolved.variables.customer_full_name;
      if (name) setContactName(name);
    }
  }, [resolved, contactName]);

  const selectedSender = senders.find((s) => s.id === selectedSenderId) || null;

  const handleSubmit = async () => {
    if (!selectedSender) {
      toast({ title: 'Errore', description: 'Seleziona un mittente SMS', variant: 'destructive' });
      return;
    }
    if (!contactPhone) {
      toast({ title: 'Errore', description: 'Inserisci il numero del contatto', variant: 'destructive' });
      return;
    }
    if (!message.trim()) {
      toast({ title: 'Errore', description: 'Inserisci il messaggio', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiClient.post<CreateConversationResponse>('/messaging/conversations', {
        contact_phone: contactPhone.startsWith('+') ? contactPhone : `+${contactPhone}`,
        contact_name: contactName || undefined,
        channel: 'sms',
        from_number: selectedSender.phone_number,
        initial_message: message,
      });
      if (res.success && res.data?.conversation_id) {
        toast({ title: 'Conversazione SMS avviata' });
        onSuccess(res.data.conversation_id);
        onClose();
      } else {
        toast({ title: 'Errore', description: res.error || 'Impossibile avviare la conversazione', variant: 'destructive' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Nuova Conversazione SMS
          </DialogTitle>
          <DialogDescription>Invia un SMS a un nuovo contatto.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Numero Mittente *</Label>
            <Select value={selectedSenderId} onValueChange={setSelectedSenderId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={senders.length === 0 ? 'Nessun numero SMS configurato' : 'Seleziona numero mittente'} />
              </SelectTrigger>
              <SelectContent>
                {senders.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5" />
                      {s.phone_number}
                      {s.label && <span className="text-muted-foreground">({s.label})</span>}
                      {s.isDefault && <Badge variant="outline" className="ml-1">Default</Badge>}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Numero Contatto *</Label>
            <div className="relative mt-1">
              <Input
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+39 333 1234567"
                className="pr-10"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                {resolving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                {!resolving && resolved?.source.customer && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                {!resolving && resolved?.source.lead && <User className="h-4 w-4 text-blue-500" />}
              </div>
            </div>
            {resolved && (resolved.source.customer || resolved.source.lead) && (
              <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                {resolved.source.customer ? (
                  <><CheckCircle2 className="h-3 w-3 text-green-500" /> Cliente trovato ({resolved.source.customer.type.toUpperCase()})</>
                ) : resolved.source.lead ? (
                  <><User className="h-3 w-3 text-blue-500" /> Lead trovato: {resolved.source.lead.title}</>
                ) : null}
              </div>
            )}
          </div>

          <div>
            <Label>Nome Contatto (opzionale)</Label>
            <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Mario Rossi" className="mt-1" />
          </div>

          <div>
            <Label>Messaggio *</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Scrivi il tuo SMS..."
              rows={4}
              className="mt-1"
            />
            <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> {message.length} caratteri
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Invia SMS
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
